// src/utils/cryptoDecimal.ts
// ตัวเลขของพอร์ตคริปโตเป็น decimal string ตรงตัว (หลังบ้านคำนวณแบบแม่นยำ)
// ไฟล์นี้มีแค่การตรวจรูปแบบและการจัดรูปแบบเพื่อแสดงผล ห้ามเอาผลของ num() ไปส่งกลับหลังบ้าน

// ข้อจำกัดจาก spec: ทศนิยมไม่เกิน 36 ตำแหน่ง, รวมไม่เกิน 60 หลักที่มีนัยสำคัญ, ยาวไม่เกิน 100 ตัวอักษร
export const CRYPTO_MAX_FRACTION_DIGITS = 36;
export const CRYPTO_MAX_SIGNIFICANT_DIGITS = 60;
export const CRYPTO_MAX_TOKEN_LENGTH = 100;

const DECIMAL_PATTERN = /^-?\d+(\.\d+)?$/;

// คืนข้อความ error ภาษาไทย หรือ null ถ้าถูกต้อง
export function validateCryptoDecimal(
  value: string,
  options: { allowNegative?: boolean; allowZero?: boolean } = {}
): string | null {
  const v = value.trim();
  if (!v) return 'กรุณากรอกตัวเลข';
  if (v.length > CRYPTO_MAX_TOKEN_LENGTH) return 'ตัวเลขยาวเกินไป';
  if (!DECIMAL_PATTERN.test(v)) return 'รูปแบบตัวเลขไม่ถูกต้อง';
  if (v.startsWith('-') && !options.allowNegative) return 'ห้ามติดลบ';

  const [intPart, fracPart = ''] = v.replace('-', '').split('.');
  if (fracPart.length > CRYPTO_MAX_FRACTION_DIGITS) {
    return `ทศนิยมได้ไม่เกิน ${CRYPTO_MAX_FRACTION_DIGITS} ตำแหน่ง`;
  }
  const significant = (intPart + fracPart).replace(/^0+/, '');
  if (significant.length > CRYPTO_MAX_SIGNIFICANT_DIGITS) {
    return `ตัวเลขรวมได้ไม่เกิน ${CRYPTO_MAX_SIGNIFICANT_DIGITS} หลัก`;
  }
  if (!options.allowZero && /^0*$/.test(intPart + fracPart)) return 'ต้องมากกว่า 0';
  return null;
}

export const isValidCryptoDecimal = (value: string, options?: { allowNegative?: boolean; allowZero?: boolean }) =>
  validateCryptoDecimal(value, options) === null;

// ปล่อยให้พิมพ์ได้เฉพาะตัวเลข จุด และเครื่องหมายลบนำหน้า (ใช้กับ onChange)
export function sanitizeDecimalInput(raw: string, allowNegative = false): string {
  let v = raw.replace(/,/g, '').replace(allowNegative ? /[^\d.-]/g : /[^\d.]/g, '');
  if (allowNegative) v = v.replace(/(?!^)-/g, '');
  const firstDot = v.indexOf('.');
  if (firstDot !== -1) v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, '');
  return v;
}

// ---------- บวก/ลบ decimal string แบบแม่นยำ (ไล่ทีละหลัก ไม่ผ่าน float) ----------
// ใช้กับยอดที่ต้องส่งให้หลังบ้าน เช่น CASH_ADJUST = ยอดเป้าหมาย - ยอดปัจจุบัน

function parseDecimal(v: string) {
  const s = String(v).trim();
  const negative = s.startsWith('-');
  const [intPart, fracPart = ''] = s.replace(/^[-+]/, '').split('.');
  return { negative, intPart: intPart || '0', fracPart };
}

// ทำให้สองค่ามีจำนวนหลักเท่ากัน แล้วคืนเป็นสตริงตัวเลขล้วน + scale
function alignDigits(a: ReturnType<typeof parseDecimal>, b: ReturnType<typeof parseDecimal>) {
  const scale = Math.max(a.fracPart.length, b.fracPart.length);
  const da = a.intPart + a.fracPart.padEnd(scale, '0');
  const db = b.intPart + b.fracPart.padEnd(scale, '0');
  const width = Math.max(da.length, db.length) + 1;
  return { x: da.padStart(width, '0'), y: db.padStart(width, '0'), scale };
}

const compareDigits = (x: string, y: string) => (x === y ? 0 : x > y ? 1 : -1); // ยาวเท่ากันเทียบแบบ string ได้

function addDigits(x: string, y: string): string {
  let carry = 0;
  let out = '';
  for (let i = x.length - 1; i >= 0; i--) {
    const d = Number(x[i]) + Number(y[i]) + carry;
    out = String(d % 10) + out;
    carry = d >= 10 ? 1 : 0;
  }
  return carry ? `1${out}` : out;
}

// x >= y เสมอ
function subDigits(x: string, y: string): string {
  let borrow = 0;
  let out = '';
  for (let i = x.length - 1; i >= 0; i--) {
    let d = Number(x[i]) - Number(y[i]) - borrow;
    borrow = d < 0 ? 1 : 0;
    if (d < 0) d += 10;
    out = String(d) + out;
  }
  return out;
}

function formatDigits(digits: string, scale: number, negative: boolean): string {
  const padded = digits.padStart(scale + 1, '0');
  const intPart = padded.slice(0, padded.length - scale).replace(/^0+(?=\d)/, '');
  const fracPart = scale ? padded.slice(padded.length - scale).replace(/0+$/, '') : '';
  const body = fracPart ? `${intPart}.${fracPart}` : intPart;
  return negative && body !== '0' ? `-${body}` : body;
}

export function addDecimal(a: string, b: string): string {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  const { x, y, scale } = alignDigits(pa, pb);
  if (pa.negative === pb.negative) return formatDigits(addDigits(x, y), scale, pa.negative);
  const cmp = compareDigits(x, y);
  if (cmp === 0) return '0';
  return cmp > 0
    ? formatDigits(subDigits(x, y), scale, pa.negative)
    : formatDigits(subDigits(y, x), scale, pb.negative);
}

export function subtractDecimal(a: string, b: string): string {
  const s = String(b).trim();
  return addDecimal(a, s.startsWith('-') ? s.slice(1) : `-${s}`);
}

// คูณแบบตั้งหลักทีละตัว (จำนวน × ราคา) ได้ผลตรงตัวเหมือนที่หลังบ้านคิด
export function multiplyDecimal(a: string, b: string): string {
  const pa = parseDecimal(a);
  const pb = parseDecimal(b);
  const da = (pa.intPart + pa.fracPart).replace(/^0+(?=\d)/, '');
  const db = (pb.intPart + pb.fracPart).replace(/^0+(?=\d)/, '');
  const result = new Array(da.length + db.length).fill(0);
  for (let i = da.length - 1; i >= 0; i--) {
    for (let j = db.length - 1; j >= 0; j--) {
      const sum = Number(da[i]) * Number(db[j]) + result[i + j + 1];
      result[i + j + 1] = sum % 10;
      result[i + j] += Math.floor(sum / 10);
    }
  }
  return formatDigits(result.join(''), pa.fracPart.length + pb.fracPart.length, pa.negative !== pb.negative);
}

// เทียบค่า: 1 ถ้า a > b, -1 ถ้า a < b, 0 ถ้าเท่ากัน
export function compareDecimal(a: string, b: string): number {
  const diff = subtractDecimal(a, b);
  if (diff === '0') return 0;
  return diff.startsWith('-') ? -1 : 1;
}

// แปลงเป็น number เพื่อแสดงผล/รวมยอดบนจอเท่านั้น (หลังบ้านอาจส่ง string หรือ number มา)
export const num = (x: unknown): number => {
  const n = Number(x);
  return Number.isFinite(n) ? n : 0;
};

// ใส่ comma ให้ส่วนจำนวนเต็มโดยไม่แตะทศนิยม (ไม่ผ่าน Number จึงไม่เสียความแม่นยำ)
function groupInteger(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// จัดรูปแบบ decimal string ตรงตัว: ตัด 0 ท้ายทศนิยม และใส่ comma (เช่น "0.000000010000000001", "12,345.67")
export function formatExactDecimal(value: unknown, minFractionDigits = 0): string {
  if (value === null || value === undefined || value === '') return '-';
  const s = String(value).trim();
  if (!DECIMAL_PATTERN.test(s)) {
    const n = Number(s);
    return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 8 }) : '-';
  }
  const negative = s.startsWith('-');
  let [intPart, fracPart = ''] = s.replace('-', '').split('.');
  fracPart = fracPart.replace(/0+$/, '');
  while (fracPart.length < minFractionDigits) fracPart += '0';
  const body = groupInteger(intPart.replace(/^0+(?=\d)/, '')) + (fracPart ? `.${fracPart}` : '');
  return negative ? `-${body}` : body;
}

// จำนวนเหรียญ: โชว์ตรงตัวทุกตำแหน่ง
export const formatCryptoQty = (value: unknown) => formatExactDecimal(value);

// ราคา/มูลค่า USD: อย่างน้อย 2 ตำแหน่ง แต่ไม่ตัดทศนิยมของเหรียญราคาต่ำทิ้ง
export const formatCryptoPrice = (value: unknown) => formatExactDecimal(value, 2);

// ย่อทศนิยมของข้อความที่จัดรูปแบบแล้วสำหรับพื้นที่แคบ: เก็บ maxDigits หลัก ที่เหลือเป็น "…"
// ถ้าเป็น 0.xxx นับหลักหลังเลข 0 นำหน้า (0.0000000000000454 ไม่กลายเป็น 0.00000…) — แสดงผลเท่านั้น
export function shortenDecimalText(text: string, maxDigits = 5): string {
  const m = /^(-?[\d,]+)\.(\d+)$/.exec(text);
  if (!m) return text;
  const [, intPart, fracPart] = m;
  const leadingZeros = /^-?0$/.test(intPart) ? (fracPart.match(/^0*/) || [''])[0].length : 0;
  const keep = leadingZeros + maxDigits;
  return fracPart.length > keep ? `${intPart}.${fracPart.slice(0, keep)}…` : text;
}

// มูลค่าที่คำนวณบนจอ (number) — ราคาต่ำกว่า $1 ต้องเห็นทศนิยมมากกว่า 2 ตำแหน่ง
// ยอดต่ำกว่า $0.01 แสดงเป็นเลขนัยสำคัญ 4 ตัว (เช่น 0.000000003721) ไม่ปัดเป็น 0.00
export function formatCryptoUsd(value: number): string {
  const n = Number.isFinite(value) ? value : 0;
  const abs = Math.abs(n);
  if (abs !== 0 && abs < 0.01) {
    return n.toLocaleString(undefined, { maximumSignificantDigits: 4 });
  }
  const digits = abs === 0 || abs >= 1 ? 2 : 4;
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: digits });
}
